import {Component, Input} from '@angular/core';
import {FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {IBaseCRUModalComponent} from '../../../../../sdk/src/components/ibase-cru-modal/ibase-cru-modal.component';
import {PrimeNgImportsModule} from '../../../../../sdk/src/prime-ng-imports';
import {SdkImportsModule} from '../../../../../sdk/src/sdk-imports';
import {FileEntryModelExtension} from '../../../api-extensions/models/file-entry.model-extension';
import {FileUtils} from '../../../utils/file.utils';
import {CpmCFileUpdateUserFileApiSchema} from '../../../../../gen/cpm-c-api-ts/src/schemas/c-common/cpmc-file.api-schemas';
import {CpmCFileApiService} from '../../../../../gen/cpm-c-api-ts/src/services/c-common/cpmc-file.api-service';
import {Observable} from 'rxjs';

export class RenameFileModalConfig {
    constructor(
        public fileEntry: FileEntryModelExtension,
        public currentFileName: string
    ) {}
}

export class RenameFileModalResult {
    constructor(public item?: FileEntryModelExtension) {}
}

/**
 * Rename file modal
 */
@Component({
    selector: 'app-rename-file-modal',
    templateUrl: './rename-file-modal.component.html',
    imports: [ReactiveFormsModule, PrimeNgImportsModule, SdkImportsModule]
})
export class RenameFileModalComponent extends IBaseCRUModalComponent<
    FileEntryModelExtension,
    RenameFileModalComponent,
    RenameFileModalConfig,
    RenameFileModalResult
> {
    @Input()
    public fileEntry?: FileEntryModelExtension;

    @Input()
    public currentFileName?: string;

    /**
     * Constructor
     */
    constructor() {
        super();

        this.configure('Rename File', (model: FileEntryModelExtension) => model.name ?? '<Unknown file>');
        this.config.width = '30rem';
        this.contentMinHeight = '4rem';
    }

    // ===============================================================
    // ===============================================================

    /**
     * On ready (Error handling is done in the parent)
     */
    protected async onReady(): Promise<void> {
        this.form = new FormGroup({
            name: new FormControl<string | null>(this.config.data?.extended?.currentFileName ?? null, [
                Validators.required,
                Validators.pattern(FileUtils.FOLDER_NAME_REGEX)
            ])
        });

        if (this.config.data?.extended?.fileEntry) this.fileEntry = this.config.data.extended.fileEntry;
        if (this.config.data?.extended?.currentFileName)
            this.currentFileName = this.config.data.extended.currentFileName;
    }
    /**
     * On destroy (Error handling and refresh is done in the parent)
     */
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    protected override async onDestroy(): Promise<void> {}

    // ===============================================================
    // ===============================================================

    /**
     * On submit
     */
    protected async onSubmit() {
        if (!this.canSubmit()) {
            return;
        }

        await this.onSubmitConfirmed();
    }
    /**
     * On submit (After potential validation)
     */
    private async onSubmitConfirmed() {
        if (!this.canSubmit() || !this.fileEntry) {
            return;
        }

        const toSubmit: CpmCFileUpdateUserFileApiSchema = {
            new_name: this.form.controls['name'].value!
        };

        await this.submitCU(
            null,
            (payload: CpmCFileUpdateUserFileApiSchema) =>
                CpmCFileApiService.patchUpdateUserFile(this.fileEntry!.uuid, payload),
            this.dummyPatchUpdateUserFile.bind(this),
            undefined,
            toSubmit,
            (resultCRUD: FileEntryModelExtension) => new RenameFileModalResult(resultCRUD)
        );
    }

    // ===============================================================
    // ===============================================================

    /**
     * Dummy proxy method
     */
    private dummyPatchUpdateUserFile(
        uuid: string,
        payload: CpmCFileUpdateUserFileApiSchema
    ): Observable<FileEntryModelExtension> {
        return CpmCFileApiService.patchUpdateUserFile(this.fileEntry!.uuid, payload);
    }
}
